import * as React from 'react';
import { useFiltersContext } from '../../contexts/filters';
import { ITaxonomy } from '../../types';

interface IProps {
	taxonomy: ITaxonomy;
}

interface ISelectOption {
	label: string;
	value: string;
}

const Checkboxes = ({ taxonomy }: IProps) => {
	const options = taxonomy.terms.map((term) => {
		// decode htmlentities.
		const label = document.createElement('textarea');
		label.innerHTML = term.name;

		return {
			label: label.value,
			value: '' + term.term_id,
		};
	});

	const { selectedTax, preSelectTaxTerm } = useFiltersContext();
	const [selectedValues, setSelectedValues] = React.useState<number[]>([]);
	const [expanded, setExpanded] = React.useState(false);
	const [tabIndex, setTabIndex] = React.useState(-1);

	const handleOnChange = (option: ISelectOption) => {
		const value = Number(option.value);
		let updatedValues;
		if (selectedValues.includes(value)) {
			updatedValues = selectedValues.filter((v) => v !== value);
		} else {
			updatedValues = [...selectedValues, value];
		}

		if (preSelectTaxTerm) {
			preSelectTaxTerm(taxonomy.name, updatedValues);
		}

		setSelectedValues(updatedValues);
	};

	const handleExpansion = () => {
		setExpanded(!expanded);
	};

	// When filters are reset, set our value back to the first option.
	React.useEffect(() => {
		if (selectedTax[taxonomy.name]) {
			setSelectedValues(selectedTax[taxonomy.name]);
		} else {
			setSelectedValues([]);
		}
	}, [selectedTax]);

	React.useEffect(() => {
		if ('age_range' === taxonomy.name || 'skin_tone' === taxonomy.name) {
			setExpanded(true);
		}

		const handleResize = () => {
			if (window.innerWidth <= 768) {
				setTabIndex(0);
			} else {
				setTabIndex(-1);
			}
		};

		handleResize(); // Set initial value
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return (
		<div className={`pfa-filter-checkboxes pfa-filter-checkboxes--${taxonomy.name}`}>
			<button
				className="pfa-filter-checkboxes__title-container btn"
				aria-expanded={expanded}
				onClick={handleExpansion}
				tabIndex={tabIndex}
			>
				<h3 className="pfa-filter-checkboxes__title">{taxonomy.labels.singular}</h3>
			</button>
			<div className="pfa-filter-checkboxes__inputs" aria-expanded={expanded}>
				{options.map((option) => {
					const regex = /(#[a-fA-F0-9]{3,6})/g;
					const matches = option.label.match(regex);
					const colorHex = matches?.length ? matches[0] : option.label;

					return (
						<label
							key={option.value}
							className="pfa-filter-checkboxes__label"
							htmlFor={`pfa-filter-checkboxes-${taxonomy.name}-${option.value}`}
							style={{ '--option-value': colorHex } as React.CSSProperties}
							tabIndex={0}
							role="checkbox"
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									handleOnChange(option);
								}
							}}
						>
							<input
								type="checkbox"
								id={`pfa-filter-checkboxes-${taxonomy.name}-${option.value}`}
								value={option.value}
								className="pfa-filter-checkboxes__input"
								onChange={() => handleOnChange(option)}
								checked={selectedValues.includes(Number(option.value)) || false}
							/>
							{'skin_tone' !== taxonomy.name && option.label}
						</label>
					);
				})}
			</div>
		</div>
	);
};

Checkboxes.displayName = 'Checkboxes';

export default Checkboxes;
